import React, { useState, useCallback } from 'react';
import { string, bool, exact, arrayOf, func, shape, number } from 'prop-types';

import { TextInput } from '@atoms/Input';
import { EditSocials, SearchListModal, ConfirmationModal } from '@molecules';
import { Main } from '@templates';
import { theme } from '@styles';
import { Typography, Flex, Button } from '@mixins';

import { ReactComponent as Dropdown } from '@assets/svgs/Dropdown.svg';

import { PhotoChanger } from '@organisms';
import { TextArea } from '@atoms';
import { BackButton } from '../EditUserProfile/styles';

const EditCompanyProfile = ({
  logo,
  onRemovePhoto,
  onSavePhoto,
  isPhotoLoading,
  error,
  onBackClick,
  isConfirmationModalOpen,
  onDiscardChanges,
  hasChanges,
  onAboutUsSave,
  onIndustrySave,
  userIndustry,
  onWebsiteChange,
  isLoading,
  aboutUs,
  website,
  companyName,
  industryList,
  socials,
  onSave,
}) => {
  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);

  const handleIndustryModalChangeState = useCallback(() => {
    setIsIndustryModalOpen(prevValue => !prevValue);
  }, []);

  const handleDiscardChanges = useCallback(() => {
    onDiscardChanges();
  }, []);

  const handleIndustrySave = useCallback(newIndustry => {
    onIndustrySave(newIndustry);
    handleIndustryModalChangeState();
  }, []);

  return (
    <Main isLoading={isLoading} error={error}>
      <Flex mb={[25, 30, 40]} width="100%" alignItems="center" justifyContent="space-between">
        <Flex flexDirection="column" justifyContent="center">
          <Flex width="fit-content" mb={[20, 25, 30]} onClick={onBackClick} pointer alignItems="center">
            <BackButton mr={['9px', 11, 13]} color={theme.color.gray[500]} SVG={Dropdown} />
            <Typography variant="diatype-400">Back</Typography>
          </Flex>
          <Typography variant="diatype-500" fontSize={[26, 30, 34]}>
            Edit Company Profile
          </Typography>
        </Flex>
        <Button
          py={['8px', 10, 12]}
          px={[40, 50, 60]}
          variant="primary"
          fontSize={[12, 14, 16]}
          onClick={onSave}
          disabled={!hasChanges}>
          Save
        </Button>
      </Flex>
      <Flex alignItems="center" flexDirection="column" width="100%">
        <Flex flexDirection="column" alignItems="center" width="100%" maxWidth={1024}>
          <Flex
            alignItems="center"
            flexDirection="column"
            paddingY={[10, 15, 20]}
            backgroundColor={theme.color.mintMinded[900]}
            width="100%"
            height={[170, 230, 270]}>
            <Flex width={[60, 100, 140]} height={[60, 100, 140]} marginBottom={20}>
              <PhotoChanger
                isLoading={isPhotoLoading}
                isEditable
                avatar={logo}
                isCompany
                onSavePhoto={onSavePhoto}
                onRemovePhoto={onRemovePhoto}
              />
            </Flex>
            <Typography
              color={theme.color.white}
              mb={[10, 15]}
              lineHeight={1}
              variant="rightserif-400"
              fontSize={[18, 25, 30, 34]}
              wordBreak="break-word">
              {companyName}
            </Typography>
          </Flex>
          <Flex
            py={[20, 30, 40]}
            px={[25, 40, 55]}
            border={`1px solid ${theme.color.gray[100]}`}
            width="100%"
            justifyContent="center">
            <Flex width="100%" flexDirection="column" maxWidth={[220, 340, 480]}>
              <Typography mb={['9px', 12, 15]} variant="rightserif-400" fontSize={[16, 20, 24]}>
                About us
              </Typography>
              <TextArea
                mb={[30, 40, 50]}
                withBorder
                height="115px"
                width="100%"
                fontSize={[12, 14, 16]}
                value={aboutUs}
                onChange={onAboutUsSave}
                placeholder="Tell us about yourself"
              />
              <Typography mb={['9px', 12, 15]} variant="rightserif-400" fontSize={[16, 20, 24]}>
                Industry
              </Typography>
              {userIndustry && (
                <Typography fontSize={[10, 14, 16]} variant="diatype-400" mb={['9px', 12, 15]}>
                  {userIndustry}
                </Typography>
              )}
              <Typography
                pointer
                onClick={handleIndustryModalChangeState}
                fontSize={[10, 14, 16]}
                variant="diatype-400"
                color={theme.color.gray[200]}
                mb={[25, 35, 45]}>
                {`+ Add${Object.keys(userIndustry).length ? ' / Edit' : ''} industry`}
              </Typography>
              <Typography mb={['9px', 12, 15]} variant="rightserif-400" fontSize={[16, 20, 24]}>
                Website
              </Typography>
              <TextInput placeholder="Enter website" value={website} onChange={onWebsiteChange} />
              <Typography mb={['9px', 12, 15]} mt={[25, 35, 45]} variant="rightserif-400" fontSize={[16, 20, 24]}>
                Social Networks
              </Typography>
              <EditSocials isCompany socials={socials} />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      {isIndustryModalOpen && (
        <SearchListModal
          withMultipleSelection={false}
          onSelect={handleIndustrySave}
          title="Industry"
          itemsList={industryList}
          isOpen={isIndustryModalOpen}
          selectedItemsList={[userIndustry]}
          onModalClose={handleIndustryModalChangeState}
        />
      )}
      <ConfirmationModal
        title="Leaving without saving?"
        text="If you quit this page now, your changes won’t be saved."
        onPrimaryButtonClick={onSave}
        onSecondaryButtonClick={handleDiscardChanges}
        isOpen={isConfirmationModalOpen}
        primaryButtonText="Save changes"
        secondaryButtonText="Discard changes"
      />
    </Main>
  );
};

EditCompanyProfile.defaultProps = {
  logo: '',
  companyName: '',
  industryList: [],
  error: {},
};

EditCompanyProfile.propTypes = {
  logo: string,
  onRemovePhoto: func.isRequired,
  onSavePhoto: func.isRequired,
  isPhotoLoading: bool.isRequired,
  error: exact({
    status: number,
    message: string,
  }),
  onBackClick: func.isRequired,
  isConfirmationModalOpen: bool.isRequired,
  onDiscardChanges: func.isRequired,
  hasChanges: bool.isRequired,
  onWebsiteChange: func.isRequired,
  onAboutUsSave: func.isRequired,
  onIndustrySave: func.isRequired,
  onSave: func.isRequired,
  socials: arrayOf(
    exact({
      name: string,
      value: string,
      placeholder: string,
      handler: func,
      icon: shape({}),
    }),
  ).isRequired,
  companyName: string,
  aboutUs: string.isRequired,
  website: string.isRequired,
  isLoading: bool.isRequired,
  industryList: arrayOf(string),
  userIndustry: string.isRequired,
};

export default EditCompanyProfile;
